import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { Reveal } from "@/components/reveal";
import { TextGenerateEffect } from "@/components/text-generate-effect";
import { ArrowRight } from "lucide-react";

export const Route = createFileRoute("/journal")({
  head: () => ({
    meta: [
      { title: "The Pluggz Journal — Creator Commerce Insights" },
      { name: "description", content: "Tips, guides and insights for creators and brands in the creator economy. Affiliate marketing, storefront building, and influencer monetization." },
      { property: "og:title", content: "The Pluggz Journal — Creator Commerce Insights" },
      { property: "og:description", content: "Tips, guides and insights for creators and brands in the creator economy. Affiliate marketing, storefront building, and influencer monetization." },
      { property: "og:url", content: `${SITE_URL}/journal` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/journal` }],
  }),
  component: JournalIndex,
});

const articles = [
  {
    to: "/journal/how-to-build-a-storefront-that-converts" as const,
    category: "Playbook",
    readTime: "7 min read",
    title: "How to build a storefront that converts",
    excerpt:
      "The layout, copy, and product mix that turn casual browsers into repeat buyers on your creator storefront.",
  },
  {
    to: "/journal/creators-guide-to-affiliate-commissions" as const,
    category: "Guide",
    readTime: "8 min read",
    title: "The creator's guide to affiliate commissions",
    excerpt:
      "How affiliate rates actually work in 2026 — tracking windows, attribution, and the math behind a sustainable creator income.",
  },
  {
    to: "/journal/why-curated-storefronts-outperform-link-in-bio" as const,
    category: "Essay",
    readTime: "5 min read",
    title: "Why curated storefronts outperform link-in-bio",
    excerpt:
      "Link-in-bio tools were built for traffic, not taste. Here's why editorial storefronts convert two to three times better.",
  },
  {
    to: "/journal/how-to-start-a-creator-storefront" as const,
    category: "Guide",
    readTime: "10 min read",
    title: "How to start a creator storefront",
    excerpt:
      "A step-by-step guide to launching a creator storefront. Compare the Amazon Influencer Program with the Pluggz editorial model and choose the right path for your audience.",
  },
  {
    to: "/journal/pluggz-vs-amazon-influencer-program" as const,
    category: "Comparison",
    readTime: "9 min read",
    title: "Pluggz vs Amazon Influencer Program",
    excerpt:
      "A side-by-side look at the Amazon Influencer Program and the Pluggz editorial storefront — conversion rates, commissions, brand trust, and where each model wins.",
  },
];

function JournalIndex() {
  return (
    <div className="relative">
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-accent/20 blur-3xl float-slow" />

      <header className="relative mx-auto max-w-3xl px-6 pt-24 pb-10 text-center">
        <Reveal>
          <p className="editorial-eyebrow">The Pluggz Journal</p>
          <h1 className="mt-4 font-serif text-5xl">
            Stories on <span className="italic shimmer">curated commerce</span>
          </h1>
        <TextGenerateEffect
          words="Long-form interviews and practical guides from the Pluggz editorial team. More launching with our July release."
          className="mx-auto mt-4 max-w-md text-muted-foreground"
          duration={0.4}
          staggerDelay={0.04}
        />
        </Reveal>
      </header>

      <section className="relative mx-auto max-w-6xl px-6 pb-24">
        <Reveal>
          <p className="editorial-eyebrow mb-6">Latest</p>
        </Reveal>
        <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {articles.map((a, i) => (
            <li key={a.to} className="h-full">
              <Reveal delay={i * 90}>
                <Link
                  to={a.to}
                  className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card via-background to-card p-8 transition-all hover:border-foreground/40 hover:shadow-lg"
                >
                  <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-accent/15 blur-3xl transition-opacity duration-700 group-hover:opacity-80" />
                  <p className="editorial-eyebrow relative">{a.category}</p>
                  <h2 className="relative mt-3 font-serif text-2xl leading-tight transition-colors group-hover:text-accent md:text-3xl">
                    {a.title}
                  </h2>
                  <p className="relative mt-3 text-sm text-muted-foreground">{a.excerpt}</p>
                  <div className="relative mt-6 flex items-center justify-between gap-4 pt-4">
                    <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{a.readTime}</span>
                    <span className="inline-flex items-center gap-2 border-b border-foreground pb-0.5 text-sm tracking-wide">
                      Read
                      <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
